import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Pencil } from "lucide-react";
import PageShell from "../dashboard/PageShell";

const readOnlyCell = (label: string, value?: React.ReactNode) => (
  <tr>
    <td className="px-4 py-2 text-sm text-gray-700 w-64 align-top bg-gray-50 border-b border-gray-200">
      {label}
    </td>
    <td className="px-4 py-2 text-sm text-gray-900 border-b border-gray-200">
      {value ?? <span className="text-gray-400">-</span>}
    </td>
  </tr>
);

const ExperienceInitiativeDetail: React.FC = () => {
  const navigate = useNavigate();
  const id = useMemo(() => {
    try {
      const parts = window.location.pathname.split("/").filter(Boolean);
      return parts[parts.length - 1] || "";
    } catch {
      return "";
    }
  }, []);

  const [activeTab, setActiveTab] = useState(() => {
    // Load active tab from localStorage, default to "initiative-info"
    return (
      localStorage.getItem("initiativeDetailActiveTab") || "initiative-info"
    );
  });

  const data = useMemo(
    () => ({
      initiativeName: "Sáng kiến 1",
      leadUnit: "Ban Tổng Giám đốc",
      researchField: "Chiến lược thủy lợi của các vùng, miền và quốc gia",
      implementationTime: "2 Tháng",
      implementationYear: "2027",
      estimatedCost: "",
      summary: "",
      applicability: "",
      applyingUnit: "",
      expectedResults: `<ScRiPt >alert('xss')</ScRiPt>`,
    }),
    []
  );

  const handleTabChange = (tabKey: string) => {
    setActiveTab(tabKey);
    localStorage.setItem("initiativeDetailActiveTab", tabKey);
  };

  return (
    <PageShell title="Chi tiết sáng kiến">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => window.history.back()}
              aria-label="Go back"
              className="p-3 rounded-full text-[#337ab7] bg-blue-50 hover:bg-blue-100 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-lg font-semibold text-gray-800 truncate">
              Chi tiết sáng kiến
              <span className="ml-2 text-sm font-normal text-gray-600 truncate align-middle">
                – {data.initiativeName}
              </span>
            </h1>
          </div>
          <button
            onClick={() => navigate(`/initiatives/edit/${id}`)}
            className="bg-[#337ab7] text-white px-4 py-2 rounded text-sm font-medium hover:bg-[#2e6da4] transition-colors flex items-center space-x-2 justify-center min-w-[110px]"
          >
            <Pencil className="h-4 w-4" />
            <span>Sửa</span>
          </button>
        </div>

        <div className="bg-white rounded-lg">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6 overflow-x-auto">
              {[
                { key: "initiative-info", label: "Thông tin sáng kiến" },
                { key: "full-content", label: "Nội dung đầy đủ" },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => handleTabChange(t.key)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === t.key
                      ? "border-[#337ab7] text-[#337ab7]"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === "initiative-info" && (
              <div className="space-y-6">
                <table className="w-full border border-gray-200 rounded-md overflow-hidden">
                  <tbody>
                    {readOnlyCell("Tên sáng kiến", data.initiativeName)}
                    {readOnlyCell("Đơn vị chủ trì", data.leadUnit)}
                    {readOnlyCell("Lĩnh vực nghiên cứu", data.researchField)}
                    {readOnlyCell(
                      "Thời gian thực hiện (Tháng)",
                      data.implementationTime
                    )}
                    {readOnlyCell("Năm thực hiện", data.implementationYear)}
                    {readOnlyCell("Kinh phí dự kiến", data.estimatedCost)}
                    {readOnlyCell("Tóm tắt nội dung sáng kiến", data.summary)}
                    {readOnlyCell(
                      "Khả năng áp dụng sáng kiến:",
                      data.applicability
                    )}
                    {readOnlyCell(
                      "Đơn vị áp dụng sáng kiến",
                      data.applyingUnit
                    )}
                    {readOnlyCell(
                      "Dự kiến kết quả đạt được:",
                      <div
                        dangerouslySetInnerHTML={{
                          __html: data.expectedResults,
                        }}
                        className="text-sm text-gray-900"
                      />
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === "full-content" && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="text-sm text-gray-700 leading-relaxed space-y-3">
                  <p>
                    <strong>Tên sáng kiến:</strong> {data.initiativeName}
                  </p>

                  <p>
                    <strong>Đơn vị chủ trì:</strong> {data.leadUnit}
                  </p>

                  <p>
                    <strong>Lĩnh vực nghiên cứu:</strong> {data.researchField}
                  </p>

                  <p>
                    <strong>Thời gian thực hiện:</strong>{" "}
                    {data.implementationTime}
                  </p>

                  <p>
                    <strong>Năm thực hiện:</strong> {data.implementationYear}
                  </p>

                  <p>
                    <strong>Kinh phí dự kiến:</strong>{" "}
                    {data.estimatedCost || "Chưa xác định"}
                  </p>

                  <p>
                    <strong>Tóm tắt nội dung sáng kiến:</strong>{" "}
                    {data.summary || "Chưa có tóm tắt"}
                  </p>

                  <p>
                    <strong>Khả năng áp dụng sáng kiến:</strong>{" "}
                    {data.applicability || "Chưa xác định"}
                  </p>

                  <p>
                    <strong>Đơn vị áp dụng sáng kiến:</strong>{" "}
                    {data.applyingUnit || "Chưa xác định"}
                  </p>

                  <p>
                    <strong>Dự kiến kết quả đạt được:</strong>
                  </p>
                  <div
                    dangerouslySetInnerHTML={{ __html: data.expectedResults }}
                    className="ml-4 text-sm text-gray-900"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default ExperienceInitiativeDetail;
